
import tidalIcon from '@tidal/styles-icons';
import { generalArrowDown, generalRightArrow } from '@tidal/styles-icons';

export default {
    components: {
        tidalIcon,
    },
    props: {
        /**
         * @property {string} items[].name
         * @property {string} [items[].value]
         * @property {string} [items[].subtitle]
         * @property {string} [items[].href]
         * @property {function} [items[].callback]
         * @property {boolean} [items[].external=false]
         * @type {import('vue').PropOptions<import('@tidal/styles-dropdown').DropdownItem[]|null>}
         */
        items: {
            type: Array,
            default: null,
        },
        active: {
            type: [String, Number, Boolean, Object, Array],  // to allow "any" type
            default: undefined,
        },
        buttonId: {
            type: String,
            default: null,
        },
        buttonSize: {
            type: String,
            default: 'medium',
            /** @param {string} val */
            validator(val) {
                return ['small', 'medium', 'regular'].includes(val);
            },
        },
        buttonType: {
            type: String,
            default: 'link',
        },
        buttonClass: {
            type: String,
            default: null,
        },
        dropdownClass: {
            type: String,
            default: null,
        },
        ariaLabel: {
            type: String,
            default: null,
        },
        animation: {
            type: Boolean,
            default: true,
        },
        indicator: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        reversed: {
            type: Boolean,
            default: false,
        },
        alignRight: {
            type: Boolean,
            default: false,
        },
        alignLeft: {
            type: Boolean,
            default: false,
        },
        hideArrow: {
            type: Boolean,
            default: false,
        },
        closeOnClick: {
            type: [String, Boolean],
            default: 'auto',
            validator(value) {
                return value === true || value === false || value === 'auto';
            },
        },
    },
    data() {
        return {
            /** @type {string?} */
            dropdownID: null,
            showDropDown: false,
            /** @type {HTMLElement | false | null} */
            mobileElement: null,
            forceAlign: false,
            offset: 0,
        };
    },
    computed: {
        /** @returns {boolean} */
        canChangePosition() {
            return this.alignLeft || this.alignRight;
        },
        /** @returns {boolean} */
        isLeft() {
            if (this.forceAlign) {
                return !this.alignLeft;
            }

            return this.alignLeft;
        },
        /** @returns {string} */
        offsetValue() {
            return `${this.offset || 0}px`;
        },
        /** @returns {string[]} */
        buttonClasses() {
            const classArray = [this.buttonClass, `btn-${this.buttonType} btn-${this.buttonSize}`];
            if (this.buttonType === 'link') {
                classArray.push('current-hover');
            }

            return classArray;
        },
        icons() {
            return {
                generalArrowDown,
                generalRightArrow,
            };
        },
    },
    watch: {
        showDropDown(visible) {
            this.$emit('popup-toggle', visible);
        },
    },
    mounted() {
        this.dropdownID = Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
    },
    beforeDestroy() {
        this.removeDropdownListener(true);
    },
    methods: {
        /**
         * @param {UIEvent} event
         * @param {import('@tidal/styles-dropdown').DropdownItem?} item
         */
        click(event, item) {
            this.$emit('click', item && item.value, event);

            if (this.closeOnClick === true || (this.closeOnClick === 'auto' && !(item && item.href))) {
                this.showDropDown = false;
                this.removeDropdownListener();
            }

            if (item && item.callback instanceof Function) {
                item.callback();
            }
        },
        /** @param {boolean} [noFocus] */
        toggleDropDown(noFocus) {
            if (this.disabled) {
                return;
            }

            this.showDropDown = !this.showDropDown;

            if (this.showDropDown) {
                this.addDropdownListener();
            } else {
                this.removeDropdownListener(noFocus);
            }
        },
        /** @param {UIEvent} e */
        dropdownListener(e) {
            const dropdownElement = /** @type {HTMLElement} */(this.$refs.dropdown);
            if (!dropdownElement.contains(/** @type {Node | null} */(e.target))) {
                this.toggleDropDown(true);
            }
        },
        addDropdownListener() {
            document.addEventListener('click', this.dropdownListener);
            document.addEventListener('keydown', this.keydownListener);
            if (this.mobileElement === null) {
                this.mobileElement = navigator.userAgent.match(/(iPad|iPhone|iPod)/g) ? document.getElementsByTagName('html')[0] : false;
            }
            if (this.mobileElement) {
                this.mobileElement.style.cursor = 'pointer';
                // @ts-ignore
                this.mobileElement.style.webkitTapHighlightColor = 'rgba(0, 0, 0, 0)';
            }

            if (this.canChangePosition) {
                window.addEventListener('resize', this.adjustDropdownPosition);
            }
        },
        /** @param {boolean} [noFocus] */
        removeDropdownListener(noFocus) {
            document.removeEventListener('click', this.dropdownListener);
            document.removeEventListener('keydown', this.keydownListener);
            if (this.mobileElement) {
                this.mobileElement.style.cursor = '';
                // @ts-ignore
                this.mobileElement.style.webkitTapHighlightColor = '';
            }
            if (!noFocus) {
                const dropdownElement = /** @type {HTMLElement} */(this.$refs.dropdown);
                const buttonElement = /** @type {HTMLButtonElement} */(dropdownElement.querySelector('.tidal-dropdown-button'));
                buttonElement && buttonElement.focus();
            }

            if (this.canChangePosition) {
                window.removeEventListener('resize', this.adjustDropdownPosition);
            }
        },
        /** @param {KeyboardEvent} e */
        keydownListener(e) {
            const keyEsc = e.keyCode === 27;
            const keyUp = e.keyCode === 38;
            const keyDown = e.keyCode === 40;

            if (!keyEsc && !keyUp && !keyDown) {
                return;
            }

            const dropdownElement = /** @type {HTMLElement} */(this.$refs.dropdown);
            if (!dropdownElement.querySelector(':focus')) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            if (keyEsc) {
                return this.toggleDropDown();
            }

            const active = dropdownElement.querySelector('.tidal-dropdown-menu--link:focus');
            if (active) {
                let foundNext = false;
                const parentElement = active.parentElement;
                if (parentElement) {
                    const next = keyDown ? parentElement.nextElementSibling : parentElement.previousElementSibling;
                    if (next) {
                        const nextElement = /** @type {HTMLAnchorElement} */(next.querySelector('.tidal-dropdown-menu--link'));
                        if (nextElement) {
                            nextElement.focus();
                            foundNext = true;
                        }
                    }
                }
                if (!foundNext) {
                    const next = keyDown ? active.nextElementSibling : active.previousElementSibling;
                    if (next && next.classList.contains('tidal-dropdown-menu--link')) {
                        /** @type {HTMLAnchorElement} */(next).focus();
                    }
                }
            } else {
                if (keyDown) {
                    const linkElement = /** @type {HTMLAnchorElement} */(dropdownElement.querySelector('.tidal-dropdown-menu--link'));
                    linkElement && linkElement.focus();
                } else {
                    const links = /** @type {NodeListOf<HTMLAnchorElement>} */(dropdownElement.querySelectorAll('.tidal-dropdown-menu--link'));
                    links[links.length - 1].focus();
                }
            }
        },
        resetDropdown() {
            this.forceAlign = false;
            this.offset = 0;
        },
        adjustDropdownPosition() {
            if (!this.canChangePosition) {
                return;
            }

            const dropdownMenu = /** @type {HTMLElement?} */(this.$refs.dropdownMenu);
            if (!dropdownMenu) {
                return;
            }

            let buttonSpaceLeft = 0;
            let buttonSpaceRight = 0;
            const minOffset = 16;
            const windowWidth = document.documentElement.clientWidth;
            const menuBounding = dropdownMenu.getBoundingClientRect();
            const isOut = (menuBounding.left < minOffset) || (menuBounding.right > windowWidth - minOffset);

            if (isOut || this.forceAlign || this.offset) {
                let hasSpaceOpposite = false;
                const dropdownButton = /** @type {HTMLElement?} */(this.$refs.dropdown);
                if (dropdownButton) {
                    const buttonBounding = dropdownButton.getBoundingClientRect();
                    buttonSpaceLeft = buttonBounding.left + buttonBounding.width;
                    buttonSpaceRight = windowWidth - buttonBounding.left - buttonBounding.width;
                    hasSpaceOpposite = (this.isLeft ? buttonSpaceLeft : buttonSpaceRight) > menuBounding.width;
                }

                if (hasSpaceOpposite) {
                    this.forceAlign = !this.forceAlign;
                    this.offset = 0;
                }

                if (hasSpaceOpposite || (!isOut && !this.offset)) {
                    return;
                }

                if ((this.isLeft && buttonSpaceRight > menuBounding.width) || (!this.isLeft && buttonSpaceLeft > menuBounding.width)) {
                    this.offset = 0;
                } else if (this.isLeft) {
                    this.offset -= menuBounding.right - windowWidth + minOffset;
                } else {
                    this.offset += menuBounding.left - minOffset;
                }
            }
        },
    },
};
